import { useStudio } from '@/components/studio/studioHooks';
import { QuestionButton, QuestionCard } from '@/components/studio/plan/Thread';
import { doingWords } from '@/lib/studio/format';
import type { StudioState } from '@/lib/studio/studioState';

const NEEDS: Record<NonNullable<StudioState['waiting']>['kind'], string> = {
  plan: 'Approve the plan below, or tell her what to change',
  review: 'Look at the video, then tell her what to change below',
  other: 'Answer her question below',
};

const scenesLine = (n: number) => `${n} scene${n === 1 ? '' : 's'}`;
const send = (type: string, conversationId: string) => window.dispatchEvent(new CustomEvent(type, { detail: { conversationId } }));

/**
 * Direction B (beta only): a video reopened in the rail opens on where things stand, before its folded history: what
 * is ready or what Ivy needs, and the likely next step. A question Ivy is waiting on is her own card just below.
 */
export function ReopenSummaryB({ conversationId }: { conversationId: string }) {
  const studio = useStudio();
  if (!studio) return null;
  const total = studio.scenes.length;
  const built = studio.scenes.filter((s) => s.status === 'built').length;
  // A finished video waiting on your review is the design's state 5: ready to review, with the ways to look at it.
  const ready = !studio.working && !studio.manifestLoading && (studio.stage === 'review' || studio.stage === 'deliver') && (!studio.waiting || studio.waiting.kind === 'review');
  // Every scene built and nothing running, but the studio has not loaded the video yet (a cold reopen).
  const assembling = !ready && !studio.working && total > 0 && built === total && (!studio.waiting || studio.waiting.kind === 'review');
  const facts = [total ? scenesLine(total) : null, studio.builtIn != null ? `built in ${Math.max(1, Math.round(studio.builtIn / 60_000))} min` : null].filter(Boolean).join(' · ');

  let title: string;
  let hint: string | undefined;
  let actions: { label: string; type: string }[] = [];
  if (studio.waiting && !ready && !assembling) {
    title = 'Ivy needs you';
    hint = NEEDS[studio.waiting.kind];
  } else if (ready) {
    title = 'Your video is ready to review';
    actions = [{ label: 'Watch from the start', type: 'studio-play-from-start' }, { label: 'Make an MP4', type: 'studio-open-publish' }, { label: 'Open the storyboard', type: 'storyboard-open' }];
  } else if (studio.stopped) {
    title = built ? `Stopped · ${built} of ${total} scenes done` : 'Stopped';
    hint = 'The reason is just below';
    if (total && !built) actions = [{ label: 'Open the storyboard', type: 'storyboard-open' }];
  } else if (assembling) {
    title = 'Putting your video together';
  } else if (studio.stage === 'build') {
    title = `Building · ${built} of ${total} scenes done`;
  } else if (studio.working) {
    title = doingWords(studio.stage, studio.notesSinceBuild);
  } else {
    title = 'The plan is ready';
    actions = [{ label: 'Open the storyboard', type: 'storyboard-open' }];
  }

  return (
    <section aria-label="Where we are" className="plan-rise">
      <p className="mb-1.5 text-b-meta text-b-t3">Where we are</p>
      <QuestionCard title={title} facts={hint ?? (facts || undefined)}>
        {actions.map((a, i) => (
          <QuestionButton key={a.label} label={a.label} recommended={i === 0} onClick={() => send(a.type, conversationId)} />
        ))}
      </QuestionCard>
    </section>
  );
}
